import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Receipt, Clock, CheckCircle, Send, Search, Trash2, Download, Plus, FileText, Copy,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-600 border-slate-200', icon: Clock },
  sent:  { label: 'Sent',  color: 'bg-blue-50 text-blue-600 border-blue-200',     icon: Send },
  paid:  { label: 'Paid',  color: 'bg-emerald-50 text-emerald-600 border-emerald-200', icon: CheckCircle },
};

export default function InvoicesList() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => { fetchInvoices(); }, []);

  const fetchInvoices = async () => {
    try {
      const res = await fetch(`${API}/invoices`, { credentials: 'include' });
      if (res.ok) setInvoices(await res.json());
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await fetch(`${API}/invoices/${deleteId}`, { method: 'DELETE', credentials: 'include' });
      setInvoices(prev => prev.filter(i => i.invoice_id !== deleteId));
      toast.success('Invoice deleted');
    } catch { toast.error('Failed to delete'); }
    setDeleteId(null);
  };

  const handleCreateBlank = async () => {
    try {
      const res = await fetch(`${API}/invoices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: 'New Invoice',
          client: {},
          sections: [{ title: 'Services', items: [{ description: '', qty: 1, unit_price: 0 }], contingencies: [] }],
          status: 'draft',
          tax_gst: 5.0,
          tax_qst: 9.975,
          deposit_paid: 0,
          payment_terms: 'Due on receipt',
          accepted_co_refs: [],
          sections_fr: [],
        }),
      });
      if (res.ok) {
        const inv = await res.json();
        toast.success(`Created ${inv.invoice_number}`);
        navigate(`/invoices/${inv.invoice_id}`);
      } else {
        toast.error('Failed to create invoice');
      }
    } catch { toast.error('Failed to create invoice'); }
  };

  const handleDuplicate = async (id, e) => {
    e.stopPropagation();
    try {
      const res = await fetch(`${API}/invoices/${id}/duplicate`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        const inv = await res.json();
        toast.success(`Duplicated as ${inv.invoice_number}`);
        navigate(`/invoices/${inv.invoice_id}`);
      } else {
        toast.error('Failed to duplicate');
      }
    } catch { toast.error('Failed to duplicate'); }
  };

  const handleStatusChange = async (id, newStatus) => {
    const prev = invoices;
    setInvoices(p => p.map(i => i.invoice_id === id ? { ...i, status: newStatus } : i));
    try {
      const res = await fetch(`${API}/invoices/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error('Failed');
      toast.success(`Marked as ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
    } catch {
      setInvoices(prev);
      toast.error('Failed to update status');
    }
  };

  const handleExportPdf = async (id, num, e) => {
    e.stopPropagation();
    try {
      const res = await fetch(`${API}/invoices/${id}/pdf`, { credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${num}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('PDF downloaded');
      }
    } catch { toast.error('Failed to export PDF'); }
  };

  const calcTotal = (inv) => {
    let sub = 0;
    (inv.sections || []).forEach(s =>
      (s.items || []).forEach(it => { sub += (it.qty || 0) * (it.unit_price || 0); })
    );
    const tax = sub * ((inv.tax_gst || 0) + (inv.tax_qst || 0)) / 100;
    return sub + tax - (inv.deposit_paid || 0);
  };

  const filtered = invoices.filter(inv => {
    const q = search.toLowerCase();
    const matchSearch = !search
      || inv.title?.toLowerCase().includes(q)
      || inv.invoice_number?.toLowerCase().includes(q)
      || inv.parent_estimate_number?.toLowerCase().includes(q)
      || inv.client?.name?.toLowerCase().includes(q);
    const matchStatus = statusFilter === 'all' || inv.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div className="animate-fade-in" data-testid="invoices-list">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D1C]" style={{ fontFamily: "'Chivo', sans-serif" }}>
            Invoices
          </h1>
          <p className="text-sm text-[#6B7A73] mt-1">
            {invoices.length} total · Billing documents for completed work
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="new-blank-invoice-btn"
            onClick={handleCreateBlank}
            className="bg-[#2D6B68] hover:bg-[#235552] text-white rounded-md px-5 py-2.5 font-medium"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Invoice
          </Button>
          <Button
            data-testid="new-invoice-btn"
            onClick={() => navigate('/estimates')}
            variant="outline"
            className="border-[#E6EBE8] text-[#1A1D1C] hover:bg-[#F5F6F5] rounded-md px-5 py-2.5 font-medium"
          >
            <FileText className="w-4 h-4 mr-2" />
            From Estimate
          </Button>
        </div>
      </div>

      <div className="flex gap-3 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7A73]" />
          <Input
            data-testid="search-invoices-input"
            placeholder="Search invoices..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 bg-white border-[#E6EBE8] rounded-md focus:border-[#2D6B68] focus:ring-1 focus:ring-[#2D6B68]"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 bg-white border-[#E6EBE8] rounded-md" data-testid="invoice-status-filter">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-[#E6EBE8] rounded-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="w-6 h-6 border-2 border-[#2D6B68] border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Receipt className="w-12 h-12 text-[#E6EBE8] mx-auto mb-4" />
            <p className="text-[#6B7A73] text-sm">
              {search || statusFilter !== 'all'
                ? 'No invoices match your filters.'
                : 'No invoices yet. Open an accepted estimate and click "Generate Invoice".'}
            </p>
          </div>
        ) : (
          <table className="w-full" data-testid="invoices-table">
            <thead>
              <tr className="bg-[#F9F9F8] border-b border-[#E6EBE8]">
                <th className="text-xs uppercase tracking-wider text-[#6B7A73] py-3 px-4 text-left font-semibold">Client</th>
                <th className="text-xs uppercase tracking-wider text-[#6B7A73] py-3 px-4 text-left font-semibold">Invoice</th>
                <th className="text-xs uppercase tracking-wider text-[#6B7A73] py-3 px-4 text-left font-semibold">From Estimate</th>
                <th className="text-xs uppercase tracking-wider text-[#6B7A73] py-3 px-4 text-left font-semibold hidden lg:table-cell">Date</th>
                <th className="text-xs uppercase tracking-wider text-[#6B7A73] py-3 px-4 text-right font-semibold">Total</th>
                <th className="text-xs uppercase tracking-wider text-[#6B7A73] py-3 px-4 text-center font-semibold">Status</th>
                <th className="text-xs uppercase tracking-wider text-[#6B7A73] py-3 px-4 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E6EBE8]">
              {filtered.map(inv => {
                const cfg = STATUS_CONFIG[inv.status] || STATUS_CONFIG.draft;
                const StatusIcon = cfg.icon;
                const total = calcTotal(inv);
                return (
                  <tr
                    key={inv.invoice_id}
                    className="hover:bg-[#F9F9F8] transition-colors cursor-pointer"
                    onClick={() => navigate(`/invoices/${inv.invoice_id}`)}
                    data-testid={`invoice-row-${inv.invoice_id}`}
                  >
                    <td className="py-3 px-4">
                      <p className="text-sm font-bold text-[#1A1D1C]">
                        {inv.client?.company || inv.client?.name || '—'}
                      </p>
                      {inv.client?.company && inv.client?.name && (
                        <p className="text-[10px] text-[#6B7A73] mt-0.5">{inv.client.name}</p>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-sm font-medium text-[#1A1D1C]">{inv.title || '—'}</p>
                      <p className="text-xs text-[#6B7A73]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                        {inv.invoice_number}
                      </p>
                    </td>
                    <td className="py-3 px-4">
                      {inv.parent_estimate_id ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/estimates/${inv.parent_estimate_id}`); }}
                          className="text-xs text-[#2D6B68] hover:underline"
                          style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                        >
                          {inv.parent_estimate_number}
                        </button>
                      ) : <span className="text-xs text-[#6B7A73]">—</span>}
                    </td>
                    <td className="py-3 px-4 hidden lg:table-cell">
                      <p className="text-sm text-[#6B7A73]">
                        {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : '—'}
                      </p>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="text-sm font-medium text-[#1A1D1C]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                        ${total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center" onClick={(e) => e.stopPropagation()}>
                      <Select
                        value={inv.status || 'draft'}
                        onValueChange={(v) => handleStatusChange(inv.invoice_id, v)}
                      >
                        <SelectTrigger
                          className={`${cfg.color} border rounded-full px-2.5 py-0.5 h-auto text-xs font-medium inline-flex items-center gap-1 w-auto min-w-0 hover:brightness-95 transition-all [&>svg]:opacity-50 [&>svg]:ml-1`}
                          data-testid={`invoice-status-trigger-${inv.invoice_id}`}
                        >
                          <StatusIcon className="w-3 h-3" />
                          <span>{cfg.label}</span>
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUS_CONFIG).map(([key, c]) => {
                            const Icon = c.icon;
                            return (
                              <SelectItem key={key} value={key} data-testid={`invoice-status-option-${key}-${inv.invoice_id}`}>
                                <span className="inline-flex items-center gap-2">
                                  <Icon className="w-3 h-3" />
                                  {c.label}
                                </span>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={(e) => handleExportPdf(inv.invoice_id, inv.invoice_number, e)}
                          className="p-1.5 rounded-md text-[#6B7A73] hover:text-[#1A1D1C] hover:bg-[#F0F3F1] transition-colors"
                          title="Export PDF"
                          data-testid={`invoice-pdf-btn-${inv.invoice_id}`}
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => handleDuplicate(inv.invoice_id, e)}
                          className="p-1.5 rounded-md text-[#6B7A73] hover:text-[#1A1D1C] hover:bg-[#F0F3F1] transition-colors"
                          title="Duplicate"
                          data-testid={`invoice-duplicate-btn-${inv.invoice_id}`}
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setDeleteId(inv.invoice_id); }}
                          className="p-1.5 rounded-md text-[#6B7A73] hover:text-red-600 hover:bg-red-50 transition-colors"
                          title="Delete"
                          data-testid={`invoice-delete-btn-${inv.invoice_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="bg-white rounded-md border border-[#E6EBE8]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>Delete Invoice?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#6B7A73]">This cannot be undone. The invoice will be permanently removed.</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteId(null)} className="border-[#E6EBE8]" data-testid="invoice-cancel-delete-btn">Cancel</Button>
            <Button onClick={handleDelete} className="bg-red-600 text-white hover:bg-red-700" data-testid="invoice-confirm-delete-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
